import { ref, computed, watch, type Ref } from 'vue'
import DOMPurify from 'dompurify'
import { marked } from 'marked'

// Configure marked with secure defaults
marked.use({
  breaks: true,  // Convert line breaks to <br>
  gfm: true,     // Enable GitHub Flavored Markdown
})

/**
 * Composable for markdown processing in Vue components
 */
export function useMarkdown(source: Ref<string>) {
  const processedContent = ref<string>('')
  const isLoading = ref<boolean>(false)

  const processContent = async () => {
    if (!source.value) {
      processedContent.value = ''
      return
    }

    isLoading.value = true
    try {
      let result = ''
      // Check if content already contains HTML tags
      const hasHtml = /<[^>]+>/.test(source.value)
      // It either has HTML or markdown
      // If it has HTML we trust it as is, otherwise we convert markdown to HTML
      // We'll sanitize afterwards in both cases
      if (hasHtml) {
        result = source.value
      } else {
        // Convert markdown to HTML
        const markedResult = marked(source.value)
        result = typeof markedResult === 'string' ? markedResult : await markedResult
      }
      
      // Sanitize HTML for security
      processedContent.value = DOMPurify.sanitize(result)
    } catch (error) {
      console.error('Markdown processing error:', error)
      processedContent.value = source.value // Fallback to original content
    } finally {
      isLoading.value = false
    }
  }

  // Auto-process when content changes
  watch(source, processContent, { immediate: true })

  return {
    processedContent: computed(() => processedContent.value),
    isLoading: computed(() => isLoading.value),
  }
}

export default useMarkdown